import {CounterLabel, UnderlineNav} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {Link} from '@github-ui/react-core/link'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {clsx} from 'clsx'
import type {NavigationCounterPageData} from '../page-data/payloads/tab-counts'
import {pullRequestsLayoutRoute} from '../routes/layout-route'
import type {NavigationUrls} from '../types/navigation-urls-types'
import styles from './PullRequestHeaderNavigation.module.css'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {ClientOnly} from './ClientOnly'
import {useHeaderTabs} from './header-tabs/header-tabs'
import type {HeaderTab} from './header-tabs/types'

interface PullRequestHeaderNavigationProps {
  activeRoute: PullRequestActiveRoute
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
}

export function PullRequestHeaderNavigation(props: PullRequestHeaderNavigationProps) {
  return (
    <ClientOnly serverFallback={<PullRequestHeaderNavigationShared {...props} labelCounts={undefined} />}>
      <PullRequestHeaderNavigationContent {...props} />
    </ClientOnly>
  )
}

export function PullRequestHeaderNavigationContent({
  activeRoute,
  pullRequest,
  repository,
  urls,
}: PullRequestHeaderNavigationProps) {
  const {data: labelCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')

  return (
    <PullRequestHeaderNavigationShared
      activeRoute={activeRoute}
      labelCounts={labelCounts}
      pullRequest={pullRequest}
      repository={repository}
      urls={urls}
    />
  )
}

function PullRequestHeaderNavigationShared({
  activeRoute,
  pullRequest,
  repository,
  urls,
  labelCounts,
}: PullRequestHeaderNavigationProps & {
  labelCounts: NavigationCounterPageData | undefined
}) {
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const tabClasses = `position-relative flex-shrink-0 text-normal ${styles.muteWhenUnselected} ${styles.overrideLineHeight}`
  const iconClasses = 'fg-muted mr-2 d-none d-sm-inline-block'

  const tabs = useHeaderTabs({pullRequest, repository, urls, labelCounts})

  if (!showUpdatedHeader) {
    return (
      <TabNav aria-label="Pull request navigation tabs" data-turbo="false">
        {tabs.map(tab => (
          <TabNavTabLink
            key={tab.key}
            tab={tab}
            activeRoute={activeRoute}
            tabClasses={tabClasses}
            iconClasses={iconClasses}
          />
        ))}
      </TabNav>
    )
  }

  return (
    <UnderlineNav aria-label="Pull request navigation tabs" className={'box-shadow-none'} data-turbo="false">
      {tabs.map(tab => (
        <UnderlineNavTabItem key={tab.key} tab={tab} activeRoute={activeRoute} tabClasses={tabClasses} />
      ))}
    </UnderlineNav>
  )
}

function formatCount(tab: HeaderTab): string {
  const value = tab.count ?? 0
  return `${value}${tab.countLimitExceeded ? '+' : ''}`
}

function TabCounter({tab}: {tab: HeaderTab}) {
  if (tab.count === undefined) return null
  // commitsCount comes from the synchronous PR payload, so it should not get the loading style.
  const isAsync = tab.key !== 'commits'
  const className = clsx('ml-2', isAsync && tab.countLoading ? styles.counterLoading : '')
  return <CounterLabel className={className}>{formatCount(tab)}</CounterLabel>
}

function TabNavTabLink({
  tab,
  activeRoute,
  tabClasses,
  iconClasses,
}: {
  tab: HeaderTab
  activeRoute: PullRequestActiveRoute
  tabClasses: string
  iconClasses: string
}) {
  const Icon = tab.icon
  const selected = activeRoute === tab.key
  const content = (
    <>
      <Icon className={iconClasses} />
      {tab.label}
      <TabCounter tab={tab} />
    </>
  )

  if (tab.kind === 'router') {
    return (
      <TabNav.Link
        as={Link}
        to={tab.to}
        preload={tab.preload}
        preloadDelay={tab.preloadDelay}
        selected={selected}
        className={tabClasses}
        id={tab.headerNavId}
      >
        {content}
      </TabNav.Link>
    )
  }

  return (
    <TabNav.Link
      href={tab.href}
      data-prefetch={tab.prefetch}
      selected={selected}
      className={tabClasses}
      id={tab.headerNavId}
    >
      {content}
    </TabNav.Link>
  )
}

function UnderlineNavTabItem({
  tab,
  activeRoute,
  tabClasses,
}: {
  tab: HeaderTab
  activeRoute: PullRequestActiveRoute
  tabClasses: string
}) {
  const Icon = tab.icon
  const ariaCurrent: 'page' | undefined = activeRoute === tab.key ? 'page' : undefined
  const content = (
    <>
      {tab.label}
      <TabCounter tab={tab} />
    </>
  )

  if (tab.kind === 'router') {
    return (
      <UnderlineNav.Item
        as={Link}
        to={tab.to}
        preload={tab.preload}
        preloadDelay={tab.preloadDelay}
        aria-current={ariaCurrent}
        className={tabClasses}
        id={tab.headerNavId}
        leadingVisual={<Icon />}
      >
        {content}
      </UnderlineNav.Item>
    )
  }

  return (
    <UnderlineNav.Item
      href={tab.href}
      data-prefetch={tab.prefetch}
      aria-current={ariaCurrent}
      className={tabClasses}
      id={tab.headerNavId}
      leadingVisual={<Icon />}
    >
      {content}
    </UnderlineNav.Item>
  )
}
